/**
 * The notices list (plan §7 M2): window, creator, path (page/cli), target apps, and the End
 * button. Used twice on the page — running/scheduled and past — so both follow the same table
 * standard (sticky header, chevron sort, drag-resizable columns; feedback_table_standard).
 */
import { useMemo } from 'react';
import { AlertTriangle, Info, Square } from 'lucide-react';
import { SortableHeader } from '../ui/SortableHeader';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useTableSort } from '../../hooks/useTableSort';
import { formatDateTimeCH } from '../../utils/format';
import type { NoticeApp, NoticeRecord, NoticeSeverity, NoticeStatus } from '../../types/notices';

type ColKey = 'id' | 'status' | 'severity' | 'title' | 'validFrom' | 'validUntil' | 'apps' | 'createdBy' | 'via' | 'ended';

const COLUMNS: { key: ColKey; label: string; width: number }[] = [
    { key: 'id', label: '#', width: 60 },
    { key: 'status', label: 'Status', width: 100 },
    { key: 'severity', label: 'Severity', width: 100 },
    { key: 'title', label: 'Notice', width: 340 },
    { key: 'validFrom', label: 'From', width: 140 },
    { key: 'validUntil', label: 'Until', width: 140 },
    { key: 'apps', label: 'Target apps', width: 180 },
    { key: 'createdBy', label: 'Created by', width: 200 },
    { key: 'via', label: 'Via', width: 70 },
    { key: 'ended', label: 'Ended early', width: 220 },
];

const SEVERITY_RANK: Record<NoticeSeverity, number> = { critical: 0, warning: 1, info: 2 };
const STATUS_RANK: Record<NoticeStatus, number> = { active: 0, scheduled: 1, ended: 2, expired: 3 };

const ACCESSORS: Record<ColKey, (n: NoticeRecord) => string | number | null> = {
    id: n => n.id,
    status: n => STATUS_RANK[n.status],
    severity: n => SEVERITY_RANK[n.severity],
    title: n => n.title,
    validFrom: n => n.validFrom,
    validUntil: n => n.validUntil,
    apps: n => n.apps.join(', ') + (n.page ? ` ${n.page}` : ''),
    createdBy: n => n.createdBy,
    via: n => n.createdVia,
    ended: n => n.expiredAt,
};

const STATUS_CHIP: Record<NoticeStatus, { label: string; cls: string }> = {
    active: { label: 'Running', cls: 'bg-green-100 text-green-800' },
    scheduled: { label: 'Scheduled', cls: 'bg-blue-100 text-blue-800' },
    ended: { label: 'Ended', cls: 'bg-gray-200 text-gray-700' },
    expired: { label: 'Expired', cls: 'bg-gray-100 text-gray-500' },
};

const SEVERITY_CHIP: Record<NoticeSeverity, string> = {
    info: 'text-blue-700',
    warning: 'text-amber-700',
    critical: 'text-red-700',
};

interface Props {
    rows: NoticeRecord[];
    apps: NoticeApp[];
    storageKey: string;
    emptyText: string;
    canEdit: boolean;
    endingId: number | null;
    onEnd?: (n: NoticeRecord) => void;
    initialSort: { key: ColKey; dir: 'asc' | 'desc' };
    maxHeightClass: string;
}

export function NoticeTable({ rows, apps, storageKey, emptyText, canEdit, endingId, onEnd, initialSort, maxHeightClass }: Props) {
    const showAction = canEdit && !!onEnd;
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLUMNS, storageKey);
    const { sorted, sort, toggleSort } = useTableSort(rows, ACCESSORS, initialSort);
    const appName = useMemo(() => new Map(apps.map(a => [a.appKey, a.appName])), [apps]);

    const headerSort = { key: (sort.key as ColKey | null), direction: sort.dir };
    const onSort = (k: ColKey) => { if (!shouldSuppressClick()) toggleSort(k); };
    const cell = 'px-3 py-2 align-top';

    if (rows.length === 0) {
        return <p className="px-4 py-6 text-center text-sm text-gray-500">{emptyText}</p>;
    }

    return (
        <div className={`overflow-auto ${maxHeightClass}`}>
            <table className="w-full table-fixed text-sm" style={{ minWidth: totalWidthOf(COLUMNS.map(c => c.key)) + (showAction ? 90 : 0) }}>
                <colgroup>
                    {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                    {showAction && <col style={{ width: 90 }} />}
                </colgroup>
                <thead>
                    <tr>
                        {COLUMNS.map(c => (
                            <SortableHeader
                                key={c.key}
                                label={c.label}
                                sortKey={c.key}
                                sort={headerSort}
                                onSort={onSort}
                                onResizeStart={x => startResize(c.key, x)}
                                onResetWidth={() => resetWidth(c.key)}
                                isResizing={resizingKey === c.key}
                            />
                        ))}
                        {showAction && <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2" />}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {sorted.map(n => {
                        const Icon = n.severity === 'info' ? Info : AlertTriangle;
                        const chip = STATUS_CHIP[n.status];
                        return (
                            <tr key={n.id} className="hover:bg-gray-50" data-testid={`notice-row-${n.id}`}>
                                <td className={`${cell} text-gray-500`}>{n.id}</td>
                                <td className={cell}>
                                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${chip.cls}`}>{chip.label}</span>
                                </td>
                                <td className={cell}>
                                    <span className={`inline-flex items-center gap-1 ${SEVERITY_CHIP[n.severity]}`}>
                                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />{n.severity}
                                    </span>
                                </td>
                                <td className={cell} title={`${n.title}\n\n${n.body}`}>
                                    <p className="truncate font-medium text-gray-900">{n.title}</p>
                                    <p className="truncate text-xs text-gray-500">{n.body}</p>
                                </td>
                                <td className={`${cell} whitespace-nowrap`}>{formatDateTimeCH(n.validFrom)}</td>
                                <td className={`${cell} whitespace-nowrap`}>{formatDateTimeCH(n.validUntil)}</td>
                                <td className={cell}>
                                    <span className="block truncate" title={n.apps.map(a => appName.get(a) ?? a).join(', ') + (n.page ? ` · ${n.page}` : '')}>
                                        {n.apps.map(a => appName.get(a) ?? a).join(', ')}
                                        {n.page && <span className="text-gray-500"> · {n.page}</span>}
                                    </span>
                                </td>
                                <td className={cell}><span className="block truncate" title={n.createdBy}>{n.createdBy}</span></td>
                                <td className={cell}>
                                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${n.createdVia === 'cli' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}>
                                        {n.createdVia}
                                    </span>
                                </td>
                                <td className={`${cell} text-xs text-gray-600`}>
                                    {n.expiredAt
                                        ? <span className="block truncate" title={`${formatDateTimeCH(n.expiredAt)} by ${n.expiredBy ?? 'unknown'}`}>{formatDateTimeCH(n.expiredAt)} · {n.expiredBy ?? 'unknown'}</span>
                                        : '–'}
                                </td>
                                {showAction && (
                                    <td className={cell}>
                                        {(n.status === 'active' || n.status === 'scheduled') && (
                                            <button
                                                type="button"
                                                onClick={() => onEnd!(n)}
                                                disabled={endingId === n.id}
                                                data-testid={`notice-end-${n.id}`}
                                                className="inline-flex items-center gap-1 rounded border border-red-300 px-2 py-0.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                                                title="End this notice now. The row stays in the list as ended."
                                            >
                                                <Square className="h-3 w-3" /> End
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
